// The order page's shipments: what each line owes and has shipped, then every
// shipment recorded against the order with its serials and a packing slip.
// Read-only; recording happens in ShipDrawer, opened by the two buttons here.
import React, { useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/table/DataTable';
import { useToast } from '../components/Toast/ToastContext';
import { buttonClass } from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import { ListRow, ListStack } from '../components/ui/ListRow';
import Panel from '../components/ui/Panel';
import Appear from '../components/ui/Appear';
import Tag from '../components/ui/Tag';
import { CATEGORY } from '../components/ui/categoryColors';
import { openAuthedPdf } from '../Receiving/openAuthedPdf';
import { formatEventDate } from '../utils/date-utils';
import type { OrderShipment, Shipping, ShippingLine, ShipmentLine } from './shipping';

interface Props {
  shipping: Shipping;
  /** Absent when this person or this order cannot ship; the button is not drawn. */
  onShip?: () => void;
  onReturn?: () => void;
  /** Leases and rentals: adds the Back column. Independent of onReturn, which also needs permission. */
  showBack?: boolean;
}

const FOLDED = 12;

/** A line's serials as chips. A return is marked in words, not by colour alone. */
const SerialChips: React.FC<{ serials: ShippingLine['serials'] }> = ({ serials }) => {
  const [all, setAll] = useState(false);
  if (serials.length === 0) return null;
  const chip = (s: ShippingLine['serials'][number], i: number) => (
    <Tag key={`${s.serial}-${s.shipment_id}-${s.direction}-${i}`} variant="code" mono size="sm">
      {s.serial}
      {s.direction === 'return' && ' (returned)'}
      {s.unmatched && ' (recorded anyway)'}
    </Tag>
  );
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {serials.slice(0, FOLDED).map(chip)}
      {serials.slice(FOLDED).map((s, i) => (
        <Appear key={`${s.serial}-${s.shipment_id}-${s.direction}-${i + FOLDED}`} show={all}>{chip(s, i + FOLDED)}</Appear>
      ))}
      {serials.length > FOLDED && (
        <button
          type="button"
          onClick={() => setAll(!all)}
          className="rounded px-1 text-[11px] text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        >
          {all ? 'Show fewer' : `Show all ${serials.length}`}
        </button>
      )}
    </div>
  );
};

// Module scope, per the DataTable contract: lines are read in the order's own order.
const SHIPPED_COLUMN: ColumnDef<ShippingLine, any> = {
  id: 'shipped',
  header: 'Shipped',
  meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
  cell: ({ row }) => row.original.shipped,
};

// Leases and rentals only: how many of the drones that went out have come back.
const BACK_COLUMN: ColumnDef<ShippingLine, any> = {
  id: 'back',
  header: 'Back',
  meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
  cell: ({ row }) => row.original.returned,
};

const REMAINING_COLUMN: ColumnDef<ShippingLine, any> = {
  id: 'remaining',
  header: 'Remaining',
  meta: { align: 'right', numeric: true },
  cell: ({ row }) =>
    row.original.remaining > 0 ? (
      <span className="text-on-surface">{row.original.remaining}</span>
    ) : (
      <span className="text-on-surface-variant">None</span>
    ),
};

const LINE_COLUMNS_HEAD: ColumnDef<ShippingLine, any>[] = [
  {
    id: 'item',
    header: 'Item',
    meta: { cellClass: 'text-on-surface' },
    cell: ({ row }) => (
      <div>
        <div>{row.original.label}</div>
        <SerialChips serials={row.original.serials} />
      </div>
    ),
  },
  { id: 'ordered', header: 'Ordered', meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' }, cell: ({ row }) => row.original.ordered },
];

const SALE_COLUMNS = [...LINE_COLUMNS_HEAD, SHIPPED_COLUMN, REMAINING_COLUMN];
const RETURNABLE_COLUMNS = [...LINE_COLUMNS_HEAD, SHIPPED_COLUMN, BACK_COLUMN, REMAINING_COLUMN];


const DIRECTION_WORD: Record<string, string> = {
  out: 'Shipped',
  return: 'Returned',
  mixed: 'Shipped and returned',
};

/** One shipment's lines folded by item and direction: serials grouped, counts summed. */
function foldLines(lines: ShipmentLine[]) {
  const groups = new Map<string, { label: string; direction: string; qty: number; serials: string[]; unmatched: boolean }>();
  for (const l of lines) {
    const key = `${l.direction}|${l.item_type}|${l.label}`;
    const g = groups.get(key) ?? { label: l.label, direction: l.direction, qty: 0, serials: [], unmatched: false };
    g.qty += l.quantity;
    if (l.drone_serial) g.serials.push(l.drone_serial);
    g.unmatched = g.unmatched || l.unmatched;
    groups.set(key, g);
  }
  return Array.from(groups.values());
}

const ShipmentsSection: React.FC<Props> = ({ shipping, onShip, onReturn, showBack }) => {
  const toast = useToast();
  const [slipBusy, setSlipBusy] = useState<string | null>(null);

  const openSlip = async (s: OrderShipment) => {
    setSlipBusy(s.id);
    try {
      // The route is authed, so a plain link would 401; the helper fetches it and opens the blob.
      await openAuthedPdf(s.slip_url || `/orders/shipments/${s.id}/slip.pdf`);
    } catch {
      toast.error('The packing slip did not open. Try again.');
    } finally {
      setSlipBusy(null);
    }
  };

  const { reading, lines, shipments } = shipping;

  const actions = (onShip || onReturn) && (
    <>
      {onShip && (
        <button type="button" onClick={onShip} className={buttonClass('tonal', 'sm')}>
          Ship
        </button>
      )}
      {onReturn && (
        <button type="button" onClick={onReturn} className={buttonClass('tonal', 'sm')}>
          Return
        </button>
      )}
    </>
  );

  return (
    <Panel title="Shipments" titleStyle="section" accent="primary" actions={actions || undefined}>
      <div className="space-y-4">
        {reading.legacy && (
          <Alert variant="note" tone="info" live={false}>
            This order was marked shipped before shipments were recorded here, so it has no serials.
          </Alert>
        )}

        {lines.length > 0 && !reading.legacy && (
          <DataTable<ShippingLine>
            data={lines}
            columns={showBack ? RETURNABLE_COLUMNS : SALE_COLUMNS}
            getRowId={(l) => String(l.sales_order_line_id)}
            on="panel"
            empty={null}
            emptyMode="replace"
          />
        )}

        <ListStack
          on="panel"
          spacing="loose"
          label="Shipments"
          items={shipments}
          getKey={(s) => s.id}
          empty={
            <EmptyState variant="line">
              Nothing has shipped on this order yet.
              {onShip && ' Ship records drones by serial and everything else by count.'}
            </EmptyState>
          }
          renderRow={(s) => (
            <ListRow>
              <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <span className="text-sm font-semibold text-on-surface">
                    {DIRECTION_WORD[s.direction ?? ''] ?? 'Recorded'}
                    {s.shipped_on ? ` ${formatEventDate(s.shipped_on)}` : ''}
                  </span>
                  {s.created_by_name && <span className="text-xs text-on-surface-variant">by {s.created_by_name}</span>}
                  {s.tracking_number && (
                    <a
                      href={`https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(s.tracking_number)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded font-mono text-xs text-blue-400 hover:text-blue-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
                    >
                      {s.tracking_number}
                    </a>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => openSlip(s)}
                  disabled={slipBusy === s.id}
                  className={buttonClass('quiet', 'sm')}
                >
                  {slipBusy === s.id ? 'Opening…' : 'Slip'}
                </button>
              </div>
              {s.notes && <p className="mt-1 text-xs text-on-surface-variant">{s.notes}</p>}
              <ul className="mt-2 space-y-1">
                {foldLines(s.lines).map((g) => (
                  <li key={`${g.direction}|${g.label}`} className="text-sm text-on-surface">
                    {g.label} <span className="tabular-nums text-on-surface-variant">x{g.qty}</span>
                    {g.direction === 'return' && s.direction === 'mixed' && <span className="text-xs text-on-surface-variant"> (returned)</span>}
                    {g.unmatched && <span className="text-xs" style={{ color: CATEGORY.orange.ink.oklch }}> (recorded anyway)</span>}
                    {g.serials.length > 0 && (
                      <div className="mt-0.5 break-words font-mono text-xs text-on-surface-variant">{g.serials.join(', ')}</div>
                    )}
                  </li>
                ))}
              </ul>
            </ListRow>
          )}
        />
      </div>
    </Panel>
  );
};

export default ShipmentsSection;
